import { Session } from './Session';

/**
 * Case Master B2C access model. The single source of truth lives on the logged-in
 * user's metadata (no extra plan endpoint):
 *
 *   user.metadata.casemasterPlanId // "free" | "pro" | "premium" | undefined
 *   user.metadata.validTill        // ISO date string (pro) | null (free/premium)
 *
 * `casemasterPlanId` is what identifies a Case Master B2C account: it is only
 * ever assigned when someone picks a Case Master plan. Do NOT use
 * `metadata.isB2cUser` for this — that flag is a generic self-serve marker also
 * set on Interview-Wizard-only accounts (see PersonalisedInterview/index.jsx,
 * where it pairs with `iwLicenses`), so gating on it would lock those users out.
 *
 * Behaviour:
 *  - NON-B2C : traditional flow — every case unlocked, unlimited attempts.
 *  - FREE    : only the flagged free case (fetched via
 *              `GET /casemasterProblem?isFreeCase=true&isActive=true`), and only
 *              until its single attempt is spent. The backend no longer meters
 *              attempts, so the frontend decides "spent" from the attempts API
 *              (any recent attempt with status `feedback_generated`).
 *  - PRO     : every case while `validTill` is in the future; once it passes,
 *              every case locks again.
 *  - PREMIUM : every case, lifetime (validTill is null).
 */
export const CaseMasterPlans = {
  FREE: 'free',
  PRO: 'pro',
  PREMIUM: 'premium',
};

// Case Master is sold on the marketing site, whose origin changes per
// environment (local / dev / prod) — only the `/case` route and its pricing
// anchor are stable. REACT_APP_LANGUIFY_PORTAL_URL is the same base the
// performance review uses to link back to the landing page.
const LANDING_CASE_ROUTE = '/case';
const PRICING_SECTION_HASH = '#pricing';
const FALLBACK_LANDING_URL = 'https://www.languify.in';

/**
 * Absolute URL of the Case Master pricing section on the marketing site.
 * @returns {string}
 */
export const getCaseMasterPricingUrl = () => {
  const base = (process.env.REACT_APP_LANGUIFY_PORTAL_URL || FALLBACK_LANDING_URL)
    .trim()
    .replace(/\/+$/, '');

  return `${base}${LANDING_CASE_ROUTE}${PRICING_SECTION_HASH}`;
};

/**
 * Read `casemasterPlanId` off the session, normalized to a known plan.
 * @returns {string|null} one of CaseMasterPlans, or null when absent/unrecognized.
 */
const readCaseMasterPlan = () => {
  const plan = Session.getUserMetadata()?.casemasterPlanId;
  const normalized = typeof plan === 'string' ? plan.trim().toLowerCase() : '';

  return Object.values(CaseMasterPlans).includes(normalized) ? normalized : null;
};

/**
 * Whether the current user is a Case Master B2C account, i.e. carries a valid
 * `casemasterPlanId`. Plan gating only applies to these users; everyone else
 * (B2B, and B2C users who never bought Case Master) keeps the traditional
 * unlimited flow.
 * @returns {boolean}
 */
export const isB2cCaseMasterUser = () => readCaseMasterPlan() !== null;

/**
 * Resolve the current user's Case Master plan, defaulting to FREE (the most
 * restrictive) when the field is missing/unknown so access fails closed. Only
 * meaningful for B2C users — callers gate on isB2cCaseMasterUser first, so the
 * fallback is unreachable in practice.
 * @returns {string} one of CaseMasterPlans
 */
export const getCaseMasterPlan = () => readCaseMasterPlan() || CaseMasterPlans.FREE;

/**
 * Whether a Pro plan's validity window is still active.
 * A missing/invalid `validTill` is treated as expired so access fails closed.
 * @param {string|number|Date|null|undefined} validTill
 * @returns {boolean}
 */
const isValidityActive = (validTill) => {
  if (!validTill) return false;

  const expiry = new Date(validTill).getTime();
  if (Number.isNaN(expiry)) return false;

  return Date.now() < expiry;
};

/**
 * Whether the current user's Pro plan has lapsed. Only meaningful for B2C Pro;
 * Premium is lifetime and Free never carries a validity.
 * @returns {boolean}
 */
export const isCaseMasterPlanExpired = () => {
  if (!isB2cCaseMasterUser()) return false;
  if (getCaseMasterPlan() !== CaseMasterPlans.PRO) return false;

  return !isValidityActive(Session.getUserMetadata()?.validTill);
};

/**
 * Whether the current user has blanket access to every case: non-B2C users
 * (traditional flow), Premium (lifetime), and Pro within its validity window.
 * Free always returns false, and an expired Pro loses access.
 * @returns {boolean}
 */
export const hasFullCaseMasterAccess = () => {
  if (!isB2cCaseMasterUser()) return true;

  const plan = getCaseMasterPlan();
  if (plan === CaseMasterPlans.PREMIUM) return true;
  if (plan === CaseMasterPlans.PRO) {
    return isValidityActive(Session.getUserMetadata()?.validTill);
  }

  return false;
};

/**
 * Decide whether a specific Case Master case is unlocked for the current user.
 *
 * @param {Object} params
 * @param {string} params.caseId         - The case master problem `_id` being checked.
 * @param {string|null} params.freeCaseId - The flagged free case `_id` (free plan only).
 * @param {boolean} params.freeAttemptUsed - Whether the free user's one attempt is spent.
 * @returns {boolean} True when the user may attempt/reattempt this case.
 */
export const isCaseMasterCaseUnlocked = ({
  caseId, freeCaseId = null, freeAttemptUsed = false,
} = {}) => {
  // Non-B2C: traditional flow, everything unlocked.
  if (!isB2cCaseMasterUser()) return true;

  const plan = getCaseMasterPlan();

  // Premium: lifetime access to every case.
  if (plan === CaseMasterPlans.PREMIUM) return true;

  // Pro: every case, but only while the validity window is still active.
  if (plan === CaseMasterPlans.PRO) {
    return isValidityActive(Session.getUserMetadata()?.validTill);
  }

  // Free: only the flagged free case, and only until its single attempt is spent.
  return (
    !!caseId &&
    !!freeCaseId &&
    String(caseId) === String(freeCaseId) &&
    !freeAttemptUsed
  );
};

/**
 * Lock state + an upgrade message for a Case Master case, ready for the UI.
 * Centralizes the access decision so the card components stay declarative.
 *
 * `linkText` is the word inside `message` that should become a link to
 * `upgradeUrl` (the landing page's pricing section) — the tooltip linkifies it
 * in place rather than appending a separate CTA line.
 *
 * @param {Object} params - same shape as isCaseMasterCaseUnlocked.
 * @returns {{ locked: boolean, message: string, upgradeUrl: string, linkText: string }}
 */
export const getCaseMasterLockInfo = ({
  caseId, freeCaseId = null, freeAttemptUsed = false,
} = {}) => {
  const locked = !isCaseMasterCaseUnlocked({ caseId, freeCaseId, freeAttemptUsed });

  if (!locked) return { locked: false, message: '', upgradeUrl: '', linkText: '' };

  const plan = getCaseMasterPlan();
  const upgradeUrl = getCaseMasterPricingUrl();
  const linkText = 'Upgrade';

  // Expired Pro (only Pro can be locked while having a plan-level validity).
  if (plan === CaseMasterPlans.PRO) {
    return {
      locked: true,
      message: 'Your Pro plan has expired. Upgrade to Premium to keep attempting cases.',
      upgradeUrl,
      linkText,
    };
  }

  // Free plan: the single free attempt has been spent.
  if (freeAttemptUsed) {
    return {
      locked: true,
      message: 'You have used your free case. Upgrade to Pro or Premium to attempt more cases.',
      upgradeUrl,
      linkText,
    };
  }

  // Free plan: any case other than the flagged free one.
  return {
    locked: true,
    message: 'Upgrade to Pro or Premium to unlock all case studies.',
    upgradeUrl,
    linkText,
  };
};
